import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useSelector, useDispatch } from 'react-redux'
import {
  useGetCompanyUserQuery,
  useGetCompanyUsersQuery,
  useDeleteCompanyUserMutation,
  setCompanyUsersSort,
  selectActiveUsersSort,
  isStandardDeveloperUser,
} from '@homeviews/store'
import type { ITheme } from '@homeviews/styles'
import {
  Table,
  Button,
  Typography,
  AccordionWrapper,
  Dialog,
  TextField,
  InfoBanner,
  Card,
  LockedContentHeader,
  WindowTitle,
} from '@homeviews/shared-ui'
import { createRows, headCells, createSkeletonRows } from '@homeviews/users'

import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import type { User } from '@homeviews/models'
import { Box } from '@material-ui/core'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      padding: theme.spacing(
        theme.layout.contentHorizontalPadding,
        theme.layout.contentVerticalPadding,
      ),
    },
    confirmationIcon: {
      transform: 'translateY(2px)',
      marginRight: 6,
    },
    accordionDetails: {
      width: '100%',
    },
    introText: {
      marginBottom: 20,
      color: theme.colors.grey800,
    },
  }),
)

interface IActiveUserState {
  id?: number
}

interface IUserToDelete {
  id?: number
  confirmationText?: string
}

const skeletonRows = createSkeletonRows()

const CompanyUsers: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const activeSort = useSelector(selectActiveUsersSort)
  const isStandardDeveloper = useSelector(isStandardDeveloperUser)

  const [userToDelete, setUserToDelete] = useState<IUserToDelete>({
    id: null,
    confirmationText: '',
  })
  const [activeUser, setActiveUser] = useState<IActiveUserState>({
    id: null,
  })

  const [deleteUser, { isLoading: isDeleting }] = useDeleteCompanyUserMutation()

  const { data: response, isFetching } = useGetCompanyUsersQuery({
    sort: activeSort,
  })

  useGetCompanyUserQuery(
    { id: activeUser?.id },
    {
      skip: !activeUser?.id,
    },
  )

  const users = response || []

  const handleGetUser = (id: number) => {
    setActiveUser({
      ...activeUser,
      id,
    })
  }

  const handleDeleteUser = (id: number) => {
    setUserToDelete({
      ...userToDelete,
      id,
    })
  }

  const confirmDeleteUser = async () => {
    try {
      await deleteUser({
        id: userToDelete?.id,
      }).unwrap()

      setUserToDelete({
        id: null,
        confirmationText: '',
      })
    } catch (err) {
      console.log(err)
    }
  }

  const rows = useMemo(
    () => createRows(users, handleGetUser, handleDeleteUser),
    [users, handleGetUser, handleDeleteUser],
  )

  const handleConfirmationTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value
    setUserToDelete({
      ...userToDelete,
      confirmationText: value,
    })
  }

  const handleSortChange = (sort: any) => {
    if (sort?.forceValue) {
      dispatch(setCompanyUsersSort({ sort: sort?.forceValue }))
      return
    }
    dispatch(setCompanyUsersSort({ sort }))
  }

  const validateDeleteConfirmation = (
    source: 'button' | 'input' = 'button',
  ): boolean => {
    if (source === 'input' && !userToDelete?.confirmationText) {
      return true
    }
    if (
      userToDelete?.confirmationText &&
      userToDelete.confirmationText.toLowerCase() === 'delete'
    ) {
      return true
    }
    return false
  }

  return (
    <>
      <WindowTitle title="Company Users" />
      <div className={classes.root}>
        {isStandardDeveloper && (
          <InfoBanner theme="gold" upgrade>
            <Typography className="l-infoBanner__title">
              Upgrade your account today
            </Typography>
          </InfoBanner>
        )}
        <div className="l-pageHeader">
          <Typography variant="h1">Users</Typography>
          {!isStandardDeveloper && (
            <Link to="/users/create">
              <Button variant="contained" color="primary" customColor="primary">
                Create user
              </Button>
            </Link>
          )}
        </div>
        <Card
          header={
            <LockedContentHeader isLocked={isStandardDeveloper}>
              <Typography variant="h3" className="l-lockedContent__title">
                User List
              </Typography>
            </LockedContentHeader>
          }
        >
          <Typography className={classes.introText}>
            Add new admin users for your business and edit their permissions
          </Typography>
          {isMobile ? (
            <AccordionWrapper>
              {!!users?.length &&
                users.map((user: User) => {
                  return (
                    <Accordion key={user.id}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                      >
                        <Typography className="l-accordionHeading">
                          {user?.name}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        <div className="c-accordionDetails">
                          <div className="c-accordionDetail">
                            <p className="c-accordionDetailLabel">Job Title</p>
                            <p className="c-accordionDetailValue">
                              {user?.job_title}
                            </p>
                          </div>
                          <div className="c-accordionDetail">
                            <p className="c-accordionDetailLabel">
                              Development
                            </p>
                            <p className="c-accordionDetailValue">
                              {user?.email}
                            </p>
                          </div>
                          <div className="c-accordionActions">
                            <Button
                              variant="outlined"
                              customColor="red"
                              onClick={() => handleDeleteUser(user?.id)}
                            >
                              Delete
                            </Button>
                            <Link to={`/edit-user/${user?.id}`}>
                              <Button
                                variant="outlined"
                                customVariant="outlinedPrimary"
                                fullWidth
                              >
                                Edit
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  )
                })}
            </AccordionWrapper>
          ) : (
            !!rows?.length && (
              <Table
                headCells={headCells}
                rows={isFetching ? skeletonRows : rows}
                activeSort={activeSort}
                onSortChange={handleSortChange}
              />
            )
          )}
          {!users?.length && !isFetching && (
            <div className="l-noRecords">No Users</div>
          )}
        </Card>
        <Dialog
          open={!!userToDelete?.id}
          dialogHeading={
            <Typography variant="h3">
              <img
                className={classes.confirmationIcon}
                src="assets/icons/medium/alert-red.svg"
                alt="Dialog icon"
              />
              Confirm deletion of user
            </Typography>
          }
          dialogContent={
            <div>
              <Box marginBottom={2}>
                <Typography />
              </Box>
              <TextField
                label="Type the word delete below and click confirm"
                value={userToDelete.confirmationText}
                onChange={handleConfirmationTextChange}
                error={!validateDeleteConfirmation('input')}
              />
            </div>
          }
          dialogActions={
            <div>
              <Button
                variant="outlined"
                customVariant="outlinedPrimary"
                onClick={() => {
                  setUserToDelete({
                    id: null,
                    confirmationText: '',
                  })
                }}
              >
                Cancel without delete
              </Button>
              <Button
                variant="contained"
                customVariant="containedPrimary"
                onClick={() => {
                  void confirmDeleteUser()
                }}
                disabled={!validateDeleteConfirmation()}
                isLoading={isDeleting}
              >
                Confirm
              </Button>
            </div>
          }
        />
      </div>
    </>
  )
}

export default CompanyUsers
